import { FC, memo, useCallback } from 'react'
import {
  ChatMessageEditorType,
  CrewChatMessageEditor,
} from 'components/elements/crewChatMessageEditor/crewChatMessageEditor'
import { useAppSelector } from 'states/hooks'
import { AiAssistantThreadMessageList } from './components/aiAssistantThreadMessageList/aiAssistantThreadMessageList'
import { useAiAssistantThread } from './useAiAssistantThread'
import { AiAssistantThreadHeader } from './components/aiAssistantThreadHeader/aiAssistantThreadHeader'
import { AiAssistantChannelDisplayType } from '@crew/enums/app'

export type AiAssistantThreadProps = {
  displayType: AiAssistantChannelDisplayType // スレッド表示のタイプ（AIアシスタント画面・AIアシスタントウィンドウ）
  selectedItemId?: string // 選択中のメッセージ。ハイライト用に使用する
}

/**
 * AIアシスタント - スレッド表示
 */
export const AiAssistantThread: FC<AiAssistantThreadProps> = memo((props) => {
  const { resetInThread } = useAiAssistantThread()

  // AIアシスタントチャンネルで表示するトピックID
  const aiAssistantCurrentChatThread = useAppSelector(
    (state) => state.message.chat.aiAssistantChannel.currentChatThread
  )

  /**
   * 戻るボタンクリック時のイベントハンドラ
   */
  const handleBackButtonClick = useCallback(() => {
    // スレッド表示を解除するためトピックIDをリセットする
    resetInThread()
  }, [resetInThread])

  // 表示するチャット投稿コンポーネントのタイプ()
  //  * AIアシスタント画面の場合は、RAG対象選択のコンポーネントを表示するため、AiAssistantPageを指定
  //  * AIアシスタントウィンドウの場合は、RAG対象選択のコンポーネントを表示しないため、AiAssistantWindowを指定
  const editorType =
    props.displayType === AiAssistantChannelDisplayType.AiAssistantPage
      ? ChatMessageEditorType.AiAssistantPage
      : ChatMessageEditorType.AiAssistantWindow

  // AIアシスタントウィンドウモードか（戻るボタンの表示制御を行う）
  const isAiAssistantWindowMode =
    props.displayType === AiAssistantChannelDisplayType.AiAssistantWindow

  // 表示に必要なデータが指定されていない場合、表示することができないので、何も表示しない
  if (!aiAssistantCurrentChatThread) {
    return null
  }

  return (
    <div className="flex flex-col w-full h-full gap-1 overflow-hidden">
      {/* ヘッダ(AIアシスタントウィンドウの時のみ表示する) */}
      {isAiAssistantWindowMode && (
        <AiAssistantThreadHeader onBackButtonClick={handleBackButtonClick} />
      )}

      {/* メッセージリスト */}
      <AiAssistantThreadMessageList
        key={aiAssistantCurrentChatThread.topicId}
        selectedItemId={props.selectedItemId}
        displayType={props.displayType}
      />

      {/* チャット投稿コンポーネント(表示しているトピックへの返信として投稿される) */}
      <CrewChatMessageEditor
        isEditMode={false}
        chatRoomId={aiAssistantCurrentChatThread.chatRoomId}
        threadRootMessageId={aiAssistantCurrentChatThread.topicId}
        rootEntityType={aiAssistantCurrentChatThread.rootEntityType}
        rootEntityRecordId={aiAssistantCurrentChatThread.rootEntityRecordId}
        canUploadFile={false}
        editorType={editorType}
      />
    </div>
  )
})
